import type { JSX, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Loader } from '../ui/Loader';

interface AuthEnsureUserProps {
  children: ReactNode;
}

/**
 * Component that ensures a user is authenticated before rendering children.
 *
 * Flow:
 * 1. First tries to use existing session (JWT + refresh tokens from cookies)
 * 2. If no session exists, creates an anonymous user via signInAnonymously
 * 3. On success: renders children
 * 4. On failure: throws error (to be caught by ErrorBoundary)
 */
export const AuthEnsureUser = ({ children }: AuthEnsureUserProps): JSX.Element => {
  const { user, isLoading, error, signInAnonymously } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [signInError, setSignInError] = useState<Error | null>(null);

  useEffect(() => {
    // If auth is still loading, wait
    if (isLoading) return;

    // If we already have a user, we're done
    if (user) return;

    // If we're already trying to sign in, don't start another attempt
    if (isSigningIn) return;

    // No user and auth is loaded - try anonymous sign in
    const attemptSignIn = async () => {
      setIsSigningIn(true);
      try {
        await signInAnonymously();
      } catch (err) {
        setSignInError(err instanceof Error ? err : new Error('Failed to create anonymous user'));
      } finally {
        setIsSigningIn(false);
      }
    };

    attemptSignIn();
  }, [user, isLoading, isSigningIn, signInAnonymously]);

  // Show loading state while auth is loading or signing in
  if (isLoading || isSigningIn) {
    return <Loader fullscreen />;
  }

  // Throw error if sign-in failed (will be caught by ErrorBoundary)
  if (signInError || error) {
    throw signInError || error;
  }

  // User is authenticated, render children
  if (user) {
    return <>{children}</>;
  }

  // Fallback loading state (shouldn't reach here normally)
  return <Loader fullscreen />;
};
